import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActiveStore } from '@/contexts/StoreContext'

interface StoreRequiredGuardProps {
    children: React.ReactNode
}

/**
 * Guard component that ensures a store exists before rendering child routes.
 * Redirects to onboarding ONLY if:
 *   - loading is complete
 *   - there was NO network/API error
 *   - stores list is confirmed empty
 *
 * If the load failed (network error, CORS, auth issue), we stay put and let
 * the user retry — we must never redirect to onboarding on a transient error.
 */
export function StoreRequiredGuard({ children }: StoreRequiredGuardProps) {
    const { activeStore: store, loading: storeLoading, stores, loadError } = useActiveStore()
    const navigate = useNavigate()

    const isLoading = storeLoading

    useEffect(() => {
        if (!isLoading && !loadError) {
            if (stores.length === 0) {
                navigate('/onboarding', { replace: true })
            }
        }
    }, [isLoading, loadError, stores, navigate])

    if (isLoading) {
        return <>{children}</>
    }

    // On load error: render children so user sees the dashboard skeleton;
    // individual queries will show their own error/retry states.
    if (loadError) {
        return <>{children}</>
    }

    // Confirmed no stores → redirect handled by useEffect; return null to avoid flash
    if (stores.length === 0) {
        return null
    }

    return <>{children}</>
}
